/**
 * useOrgTheme.ts — React hook
 * Loads org-specific white-label theme on login and applies CSS custom properties
 * to :root so all Tailwind/CSS var-based styles update automatically.
 */

import { useEffect, useCallback, useState } from 'react';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface OrgTheme {
  org_id: string;
  display_name: string | null;
  logo_url: string | null;
  favicon_url: string | null;
  primary_color: string;
  secondary_color: string;
  accent_color: string;
  background_color: string;
  surface_color: string;
  text_color: string;
  text_muted_color: string;
  font_family: string;
  font_size_base: number;
  custom_domain: string | null;
  subdomain: string | null;
  css_overrides: string | null;
  terms_url: string | null;
  privacy_url: string | null;
  support_url: string | null;
  hide_eurion_branding: boolean;
  allow_guest_access: boolean;
  // fallback flag
  _defaults?: boolean;
}

interface UseOrgThemeReturn {
  theme: OrgTheme | null;
  isLoading: boolean;
  error: string | null;
  reload: () => void;
}

// ── CSS custom property map ───────────────────────────────────────────────────

const PLATFORM_DEFAULTS: Partial<OrgTheme> = {
  primary_color: '#3B82F6',
  secondary_color: '#1E3A5F',
  accent_color: '#10B981',
  background_color: '#F8FAFC',
  surface_color: '#FFFFFF',
  text_color: '#111827',
  text_muted_color: '#6B7280',
  font_family: 'Inter, system-ui, sans-serif',
  font_size_base: 14,
};

function applyThemeToDom(theme: OrgTheme): void {
  const root = document.documentElement;

  // ── Colour CSS variables ──────────────────────────────────────────────────
  root.style.setProperty('--color-primary',     theme.primary_color     ?? PLATFORM_DEFAULTS.primary_color!);
  root.style.setProperty('--color-secondary',   theme.secondary_color   ?? PLATFORM_DEFAULTS.secondary_color!);
  root.style.setProperty('--color-accent',      theme.accent_color      ?? PLATFORM_DEFAULTS.accent_color!);
  root.style.setProperty('--color-background',  theme.background_color  ?? PLATFORM_DEFAULTS.background_color!);
  root.style.setProperty('--color-surface',     theme.surface_color     ?? PLATFORM_DEFAULTS.surface_color!);
  root.style.setProperty('--color-text',        theme.text_color        ?? PLATFORM_DEFAULTS.text_color!);
  root.style.setProperty('--color-text-muted',  theme.text_muted_color  ?? PLATFORM_DEFAULTS.text_muted_color!);

  // ── Typography ────────────────────────────────────────────────────────────
  root.style.setProperty('--font-family',  theme.font_family ?? PLATFORM_DEFAULTS.font_family!);
  root.style.setProperty('--font-size-base', `${theme.font_size_base ?? 14}px`);

  // ── Favicon ───────────────────────────────────────────────────────────────
  if (theme.favicon_url) {
    const faviconEl = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (faviconEl) {
      faviconEl.href = theme.favicon_url;
    } else {
      const link = document.createElement('link');
      link.rel = 'icon';
      link.href = theme.favicon_url;
      document.head.appendChild(link);
    }
  }

  // ── Document title ────────────────────────────────────────────────────────
  if (theme.display_name) {
    const base = theme.display_name;
    const currentTitle = document.title;
    // Append org name suffix if not already present
    if (!currentTitle.includes(base)) {
      document.title = `${currentTitle.split('—')[0].trim()} — ${base}`;
    }
  }

  // ── Custom CSS injection ──────────────────────────────────────────────────
  if (theme.css_overrides) {
    let styleEl = document.getElementById('eurion-org-custom-css') as HTMLStyleElement | null;
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = 'eurion-org-custom-css';
      document.head.appendChild(styleEl);
    }
    styleEl.textContent = theme.css_overrides;
  }
}

function resetThemeToDom(): void {
  const root = document.documentElement;
  Object.entries(PLATFORM_DEFAULTS).forEach(([key, val]) => {
    if (key.endsWith('_color')) {
      const cssVar = `--color-${key.replace('_color', '').replace('_', '-')}`;
      root.style.setProperty(cssVar, val as string);
    }
  });
  const customStyle = document.getElementById('eurion-org-custom-css');
  if (customStyle) customStyle.remove();
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useOrgTheme(orgId: string | null): UseOrgThemeReturn {
  const [theme, setTheme] = useState<OrgTheme | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!orgId) return;
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/v1/orgs/${orgId}/theme`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('eurion_token')}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data: OrgTheme = await res.json();
      setTheme(data);
      applyThemeToDom(data);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load theme';
      setError(msg);
      // Fall through to platform defaults silently
      resetThemeToDom();
    } finally {
      setIsLoading(false);
    }
  }, [orgId]);

  // Load on mount / orgId change
  useEffect(() => {
    load();
    return () => { resetThemeToDom(); };
  }, [load]);

  return { theme, isLoading, error, reload: load };
}

// ── Standalone resolver (for domain-based routing at app boot) ────────────────

export async function resolveThemeByDomain(domain: string): Promise<OrgTheme | null> {
  try {
    const res = await fetch(`/api/v1/theme/by-domain?domain=${encodeURIComponent(domain)}`);
    if (!res.ok) return null;
    const data: OrgTheme = await res.json();
    applyThemeToDom(data);
    return data;
  } catch {
    return null;
  }
}
